import { NavLink, useParams } from "react-router";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { useVbsCertificateShowQuery } from "@/services/backend/vbs/certificates/api/certificate-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { ErrDocumentNotReadyContextProvider } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { CertificatePdf } from "@/routes/processes/[processId]/vbs/certificates/[certificateId]/_components/certificate-pdf";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import { DeleteCertificateDialog } from "@/routes/processes/[processId]/vbs/certificates/[certificateId]/_components/delete-certificate-dialog";
import { useState } from "react";
import { v4 } from "uuid";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { VbsBreadcrumbs } from "@/routes/processes/[processId]/vbs/_components/vbs-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { Certificate } from "@/services/backend/vbs/certificates/types/certificate";
import { CertificateComponent } from "./_components/certificate-component";

export function CertificateRoute() {
  const { certificateId } = useParams();
  const {
    data: certificate,
    isLoading,
    error,
  } = useVbsCertificateShowQuery({
    id: certificateId!,
  });

  const breadcrumbs = certificate && (
    <VbsBreadcrumbs
      customerId={certificate.customerId}
      processId={certificate.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/vbs/${certificate.processId!}/certificates`}>
            {t("Zertifikate")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={certificate.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </VbsBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {certificate && <CertificatePage certificate={certificate} />}
    </ProcessPageMainTemplate>
  );
}

function CertificatePage({ certificate }: { certificate: Certificate }) {
  const [key, setKey] = useState(v4());

  return (
    <PersistenceContextProvider>
      <ErrDocumentNotReadyContextProvider>
        <DocumentPage>
          <DocumentImmutableAlert document={certificate} />
          <DocumentPageHeader>
            {!certificate.immutable && (
              <CpyDialog
                destinationEntityName={CpyName.VBSCertificate}
                destinationId={certificate.id}
                initialParams={{
                  process: {
                    active: !!certificate.processId,
                    values: certificate.processId
                      ? [certificate.processId]
                      : [],
                  },
                }}
                onCpySuccess={() => setKey(v4())}
              />
            )}
            <DocumentPageActions>
              {!certificate.immutable && (
                <PersistenceStateUsingContext className="mr-2" />
              )}
              <DeleteCertificateDialog certificate={certificate} />
              <CertificatePdf
                certificateId={certificate.id}
                immutable={certificate.immutable}
              />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <CertificateComponent
              certificateId={certificate.id}
              disabled={certificate.immutable}
              key={key}
            />
          </DocumentPageContent>
        </DocumentPage>
      </ErrDocumentNotReadyContextProvider>
    </PersistenceContextProvider>
  );
}
